import { BookOpen, Flag, GraduationCap, Layers, ListChecks, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { TestCompletionEndpointType, TestCompletionMode } from '@/api/testCompletion'
import type { LearnerLibraryResponse } from '@/types/coaching'
import {
  endpointTypeRequiresTarget,
  type TestCompletionEndpointOption,
} from './endpointOptions'
import { TestCompletionEndpointTree } from './TestCompletionEndpointTree'

interface Props {
  mode: TestCompletionMode
  tree: LearnerLibraryResponse['tree']
  options: TestCompletionEndpointOption[]
  selectedType: TestCompletionEndpointType | null
  endpointKey: string
  loading: boolean
  onTypeChange: (type: TestCompletionEndpointType) => void
  onEndpointChange: (endpointKey: string) => void
}

interface EndpointTypeDefinition {
  type: TestCompletionEndpointType
  icon: LucideIcon
  requiresTarget: boolean
}

const ENDPOINT_TYPES: EndpointTypeDefinition[] = [
  { type: 'LESSON', icon: BookOpen, requiresTarget: true },
  { type: 'BEFORE_NEXT_CHAPTER_TEST', icon: Flag, requiresTarget: false },
  { type: 'CHAPTER_END', icon: Layers, requiresTarget: true },
  { type: 'NEXT_CUMULATIVE_TEST', icon: ListChecks, requiresTarget: false },
  { type: 'COURSE_END', icon: GraduationCap, requiresTarget: true },
]

export function TestCompletionEndpointSelector({
  mode,
  tree,
  options,
  selectedType,
  endpointKey: selectedEndpointKey,
  loading,
  onTypeChange,
  onEndpointChange,
}: Props) {
  const { t } = useTranslation('coaching')
  const visibleTypes = mode === 'LEARNER_ACTIVITY_ONLY'
    ? ENDPOINT_TYPES.filter(({ type }) => type === 'LESSON')
    : ENDPOINT_TYPES
  const selectedDefinition = ENDPOINT_TYPES.find(({ type }) => type === selectedType)
  const targetOptions = selectedType == null
    ? []
    : options.filter((option) => option.type === selectedType)

  return (
    <fieldset className="space-y-4" aria-busy={loading}>
      <legend className="text-sm font-black text-slate-900">{t('testCompletion.endpoint.label')}</legend>
      <p className="-mt-3 text-xs leading-5 text-slate-500">{t('testCompletion.endpoint.helper')}</p>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {visibleTypes.map(({ type, icon: Icon, requiresTarget }) => {
          const optionCount = options.filter((option) => option.type === type && !option.completed).length
          const disabled = loading || optionCount === 0
          const selected = selectedType === type
          return (
            <label key={type} className={clsx('block', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
              <input
                type="radio"
                name="test-completion-endpoint-type"
                value={type}
                checked={selectedType === type}
                disabled={disabled}
                onChange={() => onTypeChange(type)}
                className="peer sr-only"
              />
              <span className={clsx(
                'flex h-full min-h-28 flex-col rounded-xl border p-3 text-left transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-cyan-600',
                disabled
                  ? 'border-slate-200 bg-slate-50 text-slate-400'
                  : 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/40',
                selected ? 'border-cyan-600 bg-cyan-50' : null,
              )}>
                <span className="flex items-start justify-between gap-2">
                  <span className={clsx(
                    'flex size-8 items-center justify-center rounded-lg',
                    selected ? 'bg-cyan-700 text-white' : 'bg-slate-100 text-slate-600',
                  )}>
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  {requiresTarget ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                      {t('testCompletion.endpoint.optionCount', { count: optionCount })}
                    </span>
                  ) : null}
                </span>
                <span className={clsx('mt-3 text-sm font-black', disabled ? 'text-slate-400' : 'text-slate-900')}>
                  {t(`testCompletion.endpoint.type.${type}.label`)}
                </span>
                <span className={clsx('mt-1 text-xs leading-5', disabled ? 'text-slate-400' : 'text-slate-500')}>
                  {t(`testCompletion.endpoint.type.${type}.description`)}
                </span>
              </span>
            </label>
          )
        })}
      </div>

      {selectedType != null && selectedDefinition?.requiresTarget ? (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-slate-800">{t('testCompletion.endpoint.targetLabel')}</h3>
          <TestCompletionEndpointTree
            key={selectedType}
            tree={tree}
            type={selectedType}
            options={targetOptions}
            selectedEndpointKey={selectedEndpointKey}
            onEndpointChange={onEndpointChange}
          />
        </div>
      ) : null}

      {selectedType && !endpointTypeRequiresTarget(selectedType) ? (
        <p className="rounded-xl border border-cyan-100 bg-cyan-50 px-3 py-2.5 text-xs font-semibold leading-5 text-cyan-900">
          {t('testCompletion.endpoint.currentBasis')}
        </p>
      ) : null}

      {loading ? (
        <p className="text-xs font-semibold text-slate-500" role="status">
          {t('testCompletion.endpoint.loading')}
        </p>
      ) : options.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs font-semibold text-slate-600" role="status">
          {t('testCompletion.endpoint.noOptions')}
        </p>
      ) : null}
    </fieldset>
  )
}
